import { app, pages } from '@microsoft/teams-js';
import {
  isMessageFromExtension,
  MessageFromApp,
  MessageTypesFromApp,
  MessageTypesFromExtension,
} from '@procureai/schemas';
import { useLayoutEffect, useState, PropsWithChildren, SetStateAction, Dispatch } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { BehaviorSubject } from 'rxjs';

import { RouteId } from '~/routes';

import { Runtime, runtimeDefaultValue, RuntimeType } from './RuntimeProvider.context';

const initializeTeams = async () => {
  try {
    await app.initialize();
    await app.getContext();
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Checks if the application is running within a browser extension context by sending an initialization message
 * and waiting for a response. Times out after 1 second if no response is received.
 *
 * @returns Promise that resolves to `true` if browser extension responds, `false` if timeout occurs after 1 second
 */
const checkForBrowserExtensionRuntime = async () =>
  new Promise((resolve) => {
    const timeoutFromBrowserExtension = setTimeout(() => {
      window.removeEventListener('message', messageEventListener);
      resolve(false);
    }, 1000);

    const messageEventListener = (event: MessageEvent<unknown>) => {
      if (
        isMessageFromExtension(event.data) &&
        event.data.type === MessageTypesFromExtension.PROCURE_AI__BROWSER_EXTENSION_INITIALISED
      ) {
        clearTimeout(timeoutFromBrowserExtension);
        window.removeEventListener('message', messageEventListener);
        resolve(true);
      }
    };

    window.addEventListener('message', messageEventListener);

    const message: MessageFromApp = { type: MessageTypesFromApp.PROCURE_AI__INITIALISE_BROWSER_EXTENSION };
    window.parent.postMessage(message, '*');
  });

const getBrowserExtensionRuntime = (setValue: Dispatch<SetStateAction<RuntimeType>>, navigate: NavigateFunction) => {
  const purchaseReqNumberStream = new BehaviorSubject<string | null>(null);

  const messageEventListener = (event: MessageEvent<unknown>) => {
    if (isMessageFromExtension(event.data)) {
      if (event.data.type === 'PROCURE_AI__PURCHASE_REQUISITION_SELECTED') {
        purchaseReqNumberStream.next(event.data.purchaseReqNumber);
      } else if (event?.data?.type === 'PROCURE_AI__NAVIGATE_TO_INTAKE_HOME') {
        navigate(RouteId.intakeCreate, { replace: true });
      }
    }
  };

  window.addEventListener('message', messageEventListener);

  setValue({
    loading: false,
    runtime: {
      type: 'browserExtension',
      data: {
        purchaseReqNumberStream,
      },
    },
  });

  return messageEventListener;
};

export const RuntimeProvider = ({ children }: PropsWithChildren) => {
  const [value, setValue] = useState<RuntimeType>(runtimeDefaultValue);
  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (!value.runtime.type) {
      if (window.parent === window) {
        setValue({ loading: false, runtime: { type: 'browser' } });
      } else {
        // when the app is embedded in an iFrame but not in MS Teams then app.getContext() takes 60 sec to time out
        // this cause the app to hanging and show a loading spinner
        // so checking for 'browserExtension' first since it is much faster (max 1 sec)
        const checkIframeContexts = async () => {
          if (await checkForBrowserExtensionRuntime()) {
            getBrowserExtensionRuntime(setValue, navigate);
          } else {
            const isTeams = await initializeTeams();
            if (isTeams) {
              setValue({ loading: false, runtime: { type: 'MSTeams' } });

              // register MS teams native back button to hook into navigation events for back navigation.
              if (pages.backStack.isSupported()) {
                pages.backStack.registerBackButtonHandler(() => {
                  if (window.history.length > 1) {
                    navigate(-1);
                    return true;
                  }

                  // in case our stack is exhausted, we fallback to the teams nav
                  void pages.backStack.navigateBack();
                  return true;
                });
              }
            } else {
              // this is the last fallback, should never happen
              setValue({ loading: false, runtime: { type: 'browser' } });
            }
          }
        };

        checkIframeContexts();
      }
    }
  }, [navigate, value.runtime.type]);

  return <Runtime.Provider value={value}>{children}</Runtime.Provider>;
};
